import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Check, X, AlertCircle, Video, User, Mail, CalendarPlus, RefreshCw, Plus } from 'lucide-react';
import AdminAppointmentBooking from './AdminAppointmentBooking';
import { toLocalYMD, formatLocalDate } from '../lib/dateHelpers';

const MentorAppointmentsManagement = () => {
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [confirmedAppointments, setConfirmedAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [processingId, setProcessingId] = useState(null);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [showAdminBookingModal, setShowAdminBookingModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ new_date: '', new_time: '', reason: '' });
  const [suggestedDates, setSuggestedDates] = useState([
    { date: '', time: '' },
    { date: '', time: '' },
    { date: '', time: '' }
  ]);
  const [suggestionMessage, setSuggestionMessage] = useState('');

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/appointments/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const appointments = await response.json();
        setPendingAppointments(appointments.filter(apt => apt.status === 'pending'));
        setConfirmedAppointments(appointments.filter(apt => apt.status === 'confirmed'));
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (appointmentId) => {
    setProcessingId(appointmentId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/appointments/${appointmentId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        const sent = (result.emails_sent || []).filter(Boolean);
        const failed = (result.emails_failed || []);
        let message = '✅ Appointment approved!';
        if (result.zoom_link) message += `\n\nZoom link:\n${result.zoom_link}`;
        if (sent.length) message += `\n\n📧 Confirmation email sent to:\n• ${sent.join('\n• ')}`;
        if (failed.length) message += `\n\n⚠️ Email FAILED for:\n• ${failed.map(f => `${f.to}: ${f.error}`).join('\n• ')}`;
        if (!sent.length && !failed.length) message += '\n\n(Email sending status unknown — check your inbox + spam.)';
        message += '\n\n(Check the spam/junk folder if the email does not appear within a minute.)';
        alert(message);
        fetchAppointments();
      } else {
        const error = await response.json();
        alert(`Failed to approve: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error approving appointment:', error);
      alert('Failed to approve appointment');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (appointmentId) => {
    const reason = prompt('Enter rejection reason (optional):');
    
    setProcessingId(appointmentId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/appointments/${appointmentId}/reject?reason=${encodeURIComponent(reason || '')}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        alert('Appointment rejected successfully');
        fetchAppointments();
      } else {
        const error = await response.json();
        alert(`Failed to reject: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error rejecting appointment:', error);
      alert('Failed to reject appointment');
    } finally {
      setProcessingId(null);
    }
  };

  const openSuggestModal = (appointment) => {
    setSelectedAppointment(appointment);
    setSuggestedDates([
      { date: '', time: '' },
      { date: '', time: '' },
      { date: '', time: '' }
    ]);
    setSuggestionMessage('');
    setShowSuggestModal(true);
  };

  const handleSuggestAlternatives = async () => {
    const validDates = suggestedDates.filter(d => d.date && d.time);
    if (validDates.length === 0) {
      alert('Please provide at least one alternative date and time');
      return;
    }

    setProcessingId(selectedAppointment.id);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/appointments/${selectedAppointment.id}/suggest-alternatives`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          suggested_dates: validDates,
          message: suggestionMessage
        })
      });

      if (response.ok) {
        alert('Alternative dates sent to the mentee!');
        setShowSuggestModal(false);
        fetchAppointments();
      } else {
        const error = await response.json();
        alert(`Failed to send suggestions: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error suggesting alternatives:', error);
      alert('Failed to send alternative dates');
    } finally {
      setProcessingId(null);
    }
  };

  // Open reschedule modal for confirmed appointments
  const openRescheduleModal = (appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleData({ new_date: '', new_time: '', reason: '' });
    setShowRescheduleModal(true);
  };

  // Handle reschedule submission
  const handleReschedule = async () => {
    if (!rescheduleData.new_date || !rescheduleData.new_time) {
      alert('Please select a new date and time');
      return;
    }
    if (!rescheduleData.reason) {
      alert('Please provide a reason for rescheduling');
      return;
    }

    setProcessingId(selectedAppointment.id);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/appointments/admin/reschedule/${selectedAppointment.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(rescheduleData)
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Appointment rescheduled successfully!\nNew date: ${result.new_date} at ${result.new_time}`);
        setShowRescheduleModal(false);
        fetchAppointments();
      } else {
        const error = await response.json();
        alert(`Failed to reschedule: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error rescheduling:', error);
      alert('Failed to reschedule appointment');
    } finally {
      setProcessingId(null);
    }
  };

  const updateSuggestedDate = (index, field, value) => {
    const newDates = [...suggestedDates];
    newDates[index][field] = value;
    setSuggestedDates(newDates);
  };

  // Generate calendar links
  const generateCalendarLinks = (appointment) => {
    const startDate = new Date(`${appointment.date}T${appointment.time}:00`);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
    
    const formatDateForGoogle = (date) => date.toISOString().replace(/-|:|\.\d\d\d/g, '');
    const formatDateForOutlook = (date) => date.toISOString();

    const title = encodeURIComponent(`Mentorship Session: ${appointment.topic || 'Meeting'}`);
    const description = encodeURIComponent(
      `Mentorship session with ${appointment.user_name || 'Mentee'}\n\nTopic: ${appointment.topic || 'Mentorship Session'}\n${appointment.zoom_link ? `Zoom Link: ${appointment.zoom_link}` : ''}`
    );
    const location = encodeURIComponent(appointment.zoom_link || 'Online (Zoom)');

    const formatICSDate = (date) => date.toISOString().replace(/-|:|\.\d\d\d/g, '').slice(0, -1) + 'Z';
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Obed Ansu Academy//Appointments//EN
BEGIN:VEVENT
DTSTART:${formatICSDate(startDate)}
DTEND:${formatICSDate(endDate)}
SUMMARY:Mentorship Session: ${appointment.topic || 'Meeting'}
DESCRIPTION:Mentorship session
LOCATION:${appointment.zoom_link || 'Online'}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });

    return {
      google: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}&details=${description}&location=${location}`,
      outlook: `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${formatDateForOutlook(startDate)}&enddt=${formatDateForOutlook(endDate)}&body=${description}&location=${location}`,
      ics: URL.createObjectURL(blob)
    };
  };

  // Render appointment card inline (not as nested component)
  const renderAppointmentCard = (appointment, showActions = true) => {
    const isProcessing = processingId === appointment.id;
    const calendarLinks = appointment.status === 'confirmed' ? generateCalendarLinks(appointment) : null;

    return (
      <div key={appointment.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{appointment.user_name || 'Anonymous'}</h3>
              <p className="text-sm text-gray-500 flex items-center space-x-1">
                <Mail className="w-3 h-3" />
                <span>{appointment.user_email}</span>
              </p>
            </div>
          </div>
          
          {appointment.status === 'pending' && (
            <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
              Pending Review
            </span>
          )}
          {appointment.status === 'confirmed' && (
            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
              Confirmed
            </span>
          )}
          {appointment.status === 'alternatives_offered' && (
            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
              Alternatives Offered
            </span>
          )}
        </div>

        {/* Details */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center space-x-3 text-sm">
            <Calendar className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <span className="text-gray-700">
              {formatLocalDate(appointment.date, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>

          <div className="flex items-center space-x-3 text-sm">
            <Clock className="w-4 h-4 text-orange-600 flex-shrink-0" />
            <span className="text-gray-700">{appointment.time}</span>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 mt-3">
            <p className="text-sm font-medium text-gray-900 mb-1">Topic:</p>
            <p className="text-sm text-gray-700">{appointment.topic}</p>
            
            {appointment.description && (
              <>
                <p className="text-sm font-medium text-gray-900 mt-2 mb-1">Description:</p>
                <p className="text-sm text-gray-600">{appointment.description}</p>
              </>
            )}
          </div>

          {appointment.zoom_link && (
            <div className="flex items-center space-x-3 text-sm">
              <Video className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <a 
                href={appointment.zoom_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Join Zoom Meeting
              </a>
            </div>
          )}
        </div>

        {/* Actions for Pending */}
        {showActions && appointment.status === 'pending' && (
          <div className="space-y-3 pt-4 border-t border-gray-200">
            <div className="flex space-x-3">
              <button
                onClick={() => handleApprove(appointment.id)}
                disabled={isProcessing}
                className="flex-1 flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                <span>{isProcessing ? 'Processing...' : 'Approve'}</span>
              </button>

              <button
                onClick={() => handleReject(appointment.id)}
                disabled={isProcessing}
                className="flex-1 flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                <span>Reject</span>
              </button>
            </div>
            
            {/* Suggest Alternative Button */}
            <button
              onClick={() => openSuggestModal(appointment)}
              disabled={isProcessing}
              className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              data-testid={`suggest-btn-${appointment.id}`}
            >
              <RefreshCw className="w-4 h-4" />
              <span>Suggest Different Dates</span>
            </button>
          </div>
        )}

        {/* Calendar Links for Confirmed */}
        {appointment.status === 'confirmed' && calendarLinks && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <CalendarPlus className="w-4 h-4 mr-2" />
              Add to Calendar:
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href={calendarLinks.google}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-1"
              >
                <span>📅</span>
                <span>Google</span>
              </a>
              <a
                href={calendarLinks.outlook}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-1"
              >
                <span>📧</span>
                <span>Outlook</span>
              </a>
              <a
                href={calendarLinks.ics}
                download={`appointment-${appointment.id}.ics`}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-1"
              >
                <span>📱</span>
                <span>Apple/Other</span>
              </a>
            </div>
            
            {/* Reschedule Button for Confirmed */}
            <div className="mt-4">
              <button
                onClick={() => openRescheduleModal(appointment)}
                className="w-full flex items-center justify-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                data-testid={`reschedule-btn-${appointment.id}`}
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reschedule Appointment</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-light text-gray-900 mb-2">Appointment Requests</h1>
            <p className="text-gray-600">Review and manage mentee appointment requests</p>
          </div>
          <button
            onClick={() => setShowAdminBookingModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            data-testid="book-for-user-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Book for User</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('pending')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'pending'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pending Requests
              {pendingAppointments.length > 0 && (
                <span className="ml-2 bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                  {pendingAppointments.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveTab('confirmed')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'confirmed'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Confirmed Appointments
              {confirmedAppointments.length > 0 && (
                <span className="ml-2 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                  {confirmedAppointments.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Pending Requests Tab */}
        {activeTab === 'pending' && (
          <div>
            {pendingAppointments.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Requests</h3>
                <p className="text-gray-600">You are all caught up! No appointment requests to review.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {pendingAppointments.map(appointment => renderAppointmentCard(appointment, true))}
              </div>
            )}
          </div>
        )}

        {/* Confirmed Appointments Tab */}
        {activeTab === 'confirmed' && (
          <div>
            {confirmedAppointments.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Confirmed Appointments</h3>
                <p className="text-gray-600">No upcoming confirmed appointments at this time.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {confirmedAppointments.map(appointment => renderAppointmentCard(appointment, false))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Suggest Alternative Dates Modal */}
      {showSuggestModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Suggest Alternative Dates</h2>
              <button 
                onClick={() => setShowSuggestModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">Original Request from <strong>{selectedAppointment.user_name}</strong>:</p>
              <p className="font-medium text-gray-900">
                {formatLocalDate(selectedAppointment.date, {
                  weekday: 'long', month: 'long', day: 'numeric'
                })} at {selectedAppointment.time}
              </p>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Provide up to 3 alternative dates/times for the mentee to choose from:
            </p>

            <div className="space-y-4 mb-6">
              {suggestedDates.map((dateTime, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
                  <input
                    type="date"
                    value={dateTime.date}
                    onChange={(e) => updateSuggestedDate(index, 'date', e.target.value)}
                    min={toLocalYMD(new Date())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <select
                    value={dateTime.time}
                    onChange={(e) => updateSuggestedDate(index, 'time', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Time</option>
                    {['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'].map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message to Mentee (optional)
              </label>
              <textarea
                value={suggestionMessage}
                onChange={(e) => setSuggestionMessage(e.target.value)}
                placeholder="e.g., I am unavailable on the requested date. Please choose one of these alternatives..."
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowSuggestModal(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSuggestAlternatives}
                disabled={processingId === selectedAppointment?.id}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {processingId === selectedAppointment?.id ? 'Sending...' : 'Send Suggestions'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Booking Modal */}
      {showAdminBookingModal && (
        <AdminAppointmentBooking
          onClose={() => setShowAdminBookingModal(false)}
          onSuccess={() => {
            setShowAdminBookingModal(false);
            fetchAppointments();
          }}
        />
      )}

      {/* Reschedule Modal for Confirmed Appointments */}
      {showRescheduleModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Reschedule Appointment</h2>
              <button 
                onClick={() => setShowRescheduleModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-600 mb-1">Current Appointment:</p>
              <p className="font-medium text-red-800">
                {selectedAppointment.user_name} - {formatLocalDate(selectedAppointment.date, {
                  weekday: 'long', month: 'long', day: 'numeric'
                })} at {selectedAppointment.time}
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Date</label>
                <input
                  type="date"
                  value={rescheduleData.new_date}
                  onChange={(e) => setRescheduleData({...rescheduleData, new_date: e.target.value})}
                  min={toLocalYMD(new Date())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Time</label>
                <select
                  value={rescheduleData.new_time}
                  onChange={(e) => setRescheduleData({...rescheduleData, new_time: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select Time</option>
                  {['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'].map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Rescheduling *</label>
                <textarea
                  value={rescheduleData.reason}
                  onChange={(e) => setRescheduleData({...rescheduleData, reason: e.target.value})}
                  placeholder="e.g., Schedule conflict, mentee requested change..."
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowRescheduleModal(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleReschedule}
                disabled={processingId === selectedAppointment?.id}
                className="flex-1 px-4 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50"
              >
                {processingId === selectedAppointment?.id ? 'Rescheduling...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentorAppointmentsManagement;
